/**
 * Ventes BtoB : statistiques de la période, et écritures.
 *
 * Les écritures invalident TOUTES les entrées `btob-stats` (toutes périodes
 * confondues) : le Résumé lit la même requête, et une vente saisie doit y
 * compter sans rechargement.
 */

import type { BtobSale, BtobSaleInput, BtobStats } from '@shared/types/btob';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, createBtobSale, deleteBtobSale, fetchBtobStats, updateBtobSale } from '../lib/api';
import { useAuth } from './useAuth';
import { useSectionStats, type SectionStatsOptions } from './useSectionStats';

const BTOB_KEY = 'btob-stats';

export function useBtobStats(options: SectionStatsOptions) {
  return useSectionStats<BtobStats>(BTOB_KEY, fetchBtobStats, options);
}

export function useBtobMutations() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const requireToken = (): string => {
    if (!token) throw new ApiRequestError(401, 'Session expiree');
    return token;
  };
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [BTOB_KEY] });
  };

  const create = useMutation<BtobSale, Error, BtobSaleInput>({
    mutationFn: async (input) => (await createBtobSale(input, requireToken())).sale,
    onSuccess: invalidate,
  });
  const update = useMutation<BtobSale, Error, { id: number; input: BtobSaleInput }>({
    mutationFn: async ({ id, input }) => (await updateBtobSale(id, input, requireToken())).sale,
    onSuccess: invalidate,
  });
  const remove = useMutation<void, Error, number>({
    mutationFn: (id) => deleteBtobSale(id, requireToken()),
    onSuccess: invalidate,
  });

  return {
    create: (input: BtobSaleInput) => create.mutateAsync(input),
    update: (id: number, input: BtobSaleInput) => update.mutateAsync({ id, input }),
    remove: (id: number) => remove.mutateAsync(id),
    isSaving: create.isPending || update.isPending || remove.isPending,
  };
}
